import { createContext, PropsWithChildren, useMemo, ReactElement, useContext, useState, useCallback } from 'react';
import { ErrorNotification } from 'app/shared/types/errors.types';

interface ErrorsContextValue {
  errors: Array<ErrorNotification>;
  hideErrors: (queryKey?: string) => void;
  addError: (queryKey: string, error: ErrorNotification) => void;
  genericErrors: Array<ErrorNotification>;
  hideGenericErrors: (queryKey?: string) => void;
  addGenericError: (queryKey: string, error: ErrorNotification) => void;
}

const ErrorsContext = createContext<ErrorsContextValue | null>(null);

export const ErrorsProvider = ({
  children,
}: PropsWithChildren): ReactElement => {
  // Generic errors for the whole app
  const [genericErrors, setGenericErrors] = useState<{ queryKey: string, error: ErrorNotification }[]>([]);

  const hideGenericErrors = useCallback((queryKey?: string) => {
    if (queryKey) {
      setGenericErrors((prevErrors) => prevErrors.filter((error) => error.queryKey !== queryKey));
    } else {
      setGenericErrors([]);
    }
  }, []);

  const addGenericError = useCallback((queryKey: string, error: ErrorNotification) => {
    setGenericErrors((prevErrors) => [...prevErrors, { queryKey, error }]);
  }, []);

  // Functional errors for a non-critical action
  const [errors, setErrors] = useState<{ queryKey: string, error: ErrorNotification }[]>([]);

  const hideErrors = useCallback((queryKey?: string) => {
    if (queryKey) {
      setErrors((prevErrors) => prevErrors.filter((error) => error.queryKey !== queryKey));
    } else {
      setErrors([]);
    }
  }, []);

  const addError = useCallback((queryKey: string, error: ErrorNotification) => {
    setErrors((prevErrors) => [...prevErrors, { queryKey, error }]);
  }, []);

  const contextValue = useMemo(() => ({
    errors: errors.map((error) => error.error),
    hideErrors,
    addError,
    genericErrors: genericErrors.map((error) => error.error),
    hideGenericErrors,
    addGenericError,
  }), [genericErrors, errors, hideErrors, hideGenericErrors, addError, addGenericError]);

  return <ErrorsContext.Provider value={contextValue}>{children}</ErrorsContext.Provider>;
};

export function useErrors(): ErrorsContextValue {
  const context = useContext(ErrorsContext);
  if (!context) {
    throw new Error('useErrors must be used within a ErrorsProvider');
  }
  return context;
}
